import {LitElement, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {Service} from '../../service/service.js';
import {ElementUtil} from '../../utils/elementUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {BaseComponent} from '../basecomponent.js';

/* global customElements */

/**
 * Report option: month/quarter (IFTA-backed) OR free-form start/end date range.
 * Used by Fuel Tax Chargeback for lease-end / partial-period runs.
 */
class OptionPeriodOrDateRange extends BaseComponent {

	static get properties() {
		return {
			required: {type: Boolean},
			name: {type: String}
		};
	}

	constructor() {
		super();
		this.required = false;
		this.name = '';
		this.mode = 'PERIOD';

		var periodOption = this;
		setTimeout(function() {
			periodOption.normalizeBooleanFlags();
			periodOption.init();
		});
	}

	normalizeBooleanFlags() {
		var periodOption = this;
		var attributeValue = periodOption.getAttribute('required');
		if (attributeValue === 'false' || attributeValue === '0') {
			periodOption.required = false;
			periodOption.removeAttribute('required');
		} else if (attributeValue === 'true' || attributeValue === '') {
			periodOption.required = true;
		}
	}

	render() {
		return html`<slot></slot>`;
	}

	init() {
		var periodOption = this;
		var yearModel = {};

		periodOption.modeCombo = ElementUtil.create({
			tag: 'vaadin-combo-box',
			'item-value-path': 'code',
			'item-label-path': 'description',
			style: 'width: 100%; margin-bottom: 10px;',
			label: 'Period Type' + (periodOption.required ? ' *' : ''),
			name: periodOption.name + '-mode',
			listeners: {
				change: function() {
					periodOption.mode = periodOption.modeCombo.value || 'PERIOD';
					periodOption.updateModeVisibility();
					periodOption.clearValidationErrors();
				}
			}
		});
		periodOption.modeCombo.items = [
			{code: 'PERIOD', description: 'Month / Quarter'},
			{code: 'DATE_RANGE', description: 'Date Range'}
		];
		periodOption.modeCombo.value = 'PERIOD';

		periodOption.year = ElementUtil.create({
			tag: 'vaadin-combo-box',
			'item-value-path': 'code',
			'item-label-path': 'description',
			style: 'width: 25%; margin-right: 10px;',
			label: 'Year' + (periodOption.required ? ' *' : ''),
			name: periodOption.name + '-year',
			listeners: {
				change: function() {
					var newYear = periodOption.year.value;
					if (newYear == null || newYear === '') {
						return;
					}
					periodOption.month.value = null;
					periodOption.month.items = yearModel[newYear].months;
					periodOption.quarter.value = null;
					periodOption.quarter.items = yearModel[newYear].quarters;
				}
			}
		});

		periodOption.month = ElementUtil.create({
			tag: 'vaadin-combo-box',
			'item-value-path': 'code',
			'item-label-path': 'description',
			style: 'flex: 1; margin-right: 10px;',
			label: 'Month *',
			name: periodOption.name + '-month',
			listeners: {
				change: function() {
					if (periodOption.month.value) {
						periodOption.quarter.disabled = true;
					} else {
						periodOption.quarter.disabled = false;
					}
					periodOption.clearMonthQuarterInvalid();
				}
			}
		});

		periodOption.quarter = ElementUtil.create({
			tag: 'vaadin-combo-box',
			'item-value-path': 'code',
			'item-label-path': 'description',
			style: 'flex: 2;',
			label: 'Quarter *',
			name: periodOption.name + '-quarter',
			listeners: {
				change: function() {
					if (periodOption.quarter.value) {
						periodOption.month.disabled = true;
					} else {
						periodOption.month.disabled = false;
					}
					periodOption.clearMonthQuarterInvalid();
				}
			}
		});

		periodOption.periodRow = ElementUtil.create({
			tag: 'div',
			style: 'width: 100%; display: flex; flex-direction: row;',
			children: [
				periodOption.year,
				periodOption.month,
				periodOption.quarter
			]
		});

		periodOption.startDate = ElementUtil.create({
			tag: 'vaadin-date-picker',
			style: 'flex: 1; margin-right: 10px;',
			label: 'Start Date' + (periodOption.required ? ' *' : ''),
			name: periodOption.name + '-startDate',
			listeners: {
				change: function() {
					periodOption.clearDateRangeInvalid();
				}
			}
		});

		periodOption.endDate = ElementUtil.create({
			tag: 'vaadin-date-picker',
			style: 'flex: 1;',
			label: 'End Date' + (periodOption.required ? ' *' : ''),
			name: periodOption.name + '-endDate',
			listeners: {
				change: function() {
					periodOption.clearDateRangeInvalid();
				}
			}
		});

		periodOption.dateRangeRow = ElementUtil.create({
			tag: 'div',
			style: 'width: 100%; display: none; flex-direction: row;',
			children: [
				periodOption.startDate,
				periodOption.endDate
			]
		});

		var container = ElementUtil.create({
			tag: 'div',
			style: 'width: 100%;',
			children: [
				periodOption.modeCombo,
				periodOption.periodRow,
				periodOption.dateRangeRow
			]
		});

		periodOption.append(container);
		periodOption.updateModeVisibility();

		Service.getReportService().getYearMonthQuarter(false).then(function(data) {
			var years = [];
			data.forEach(function(item) {
				var yearCode = '' + item.year;
				years.push({
					code: yearCode,
					description: yearCode
				});
				yearModel[yearCode] = {
					year: yearCode,
					months: item.months,
					quarters: item.quarters
				};
			});
			periodOption.year.items = years;
			periodOption.year.value = '' + (new Date()).getFullYear();

			if (periodOption.required) {
				periodOption.year.required = true;
				periodOption.modeCombo.required = true;
			}

			var event = new Event('change');
			periodOption.year.dispatchEvent(event);

			var readyEvent = new CustomEvent('ready', {detail: ''});
			periodOption.dispatchEvent(readyEvent);
		});
	}

	updateModeVisibility() {
		var periodOption = this;
		var isDateRange = periodOption.mode === 'DATE_RANGE';
		if (periodOption.periodRow) {
			periodOption.periodRow.style.display = isDateRange ? 'none' : 'flex';
		}
		if (periodOption.dateRangeRow) {
			periodOption.dateRangeRow.style.display = isDateRange ? 'flex' : 'none';
		}
	}

	getSelectedCode(combo) {
		if (!combo) {
			return null;
		}
		if (combo.value != null && combo.value !== '') {
			return '' + combo.value;
		}
		if (combo.selectedItem && combo.selectedItem.code != null && combo.selectedItem.code !== '') {
			return '' + combo.selectedItem.code;
		}
		return null;
	}

	clearMonthQuarterInvalid() {
		var periodOption = this;
		if (periodOption.month) {
			periodOption.month.invalid = false;
			periodOption.month.errorMessage = '';
		}
		if (periodOption.quarter) {
			periodOption.quarter.invalid = false;
			periodOption.quarter.errorMessage = '';
		}
	}

	clearDateRangeInvalid() {
		var periodOption = this;
		if (periodOption.startDate) {
			periodOption.startDate.invalid = false;
			periodOption.startDate.errorMessage = '';
		}
		if (periodOption.endDate) {
			periodOption.endDate.invalid = false;
			periodOption.endDate.errorMessage = '';
		}
	}

	clearValidationErrors() {
		this.clearMonthQuarterInvalid();
		this.clearDateRangeInvalid();
	}

	/**
	 * Used by dl-form-panel.isValid().
	 */
	validate() {
		var periodOption = this;
		periodOption.normalizeBooleanFlags();
		var valid = true;
		var mode = periodOption.getSelectedCode(periodOption.modeCombo) || periodOption.mode || 'PERIOD';

		if (mode === 'DATE_RANGE') {
			var startValue = periodOption.startDate ? periodOption.startDate.value : null;
			var endValue = periodOption.endDate ? periodOption.endDate.value : null;
			if (!startValue) {
				valid = false;
				if (periodOption.startDate) {
					periodOption.startDate.invalid = true;
					periodOption.startDate.errorMessage = 'Start date is required';
				}
			}
			if (!endValue) {
				valid = false;
				if (periodOption.endDate) {
					periodOption.endDate.invalid = true;
					periodOption.endDate.errorMessage = 'End date is required';
				}
			}
			if (startValue && endValue && startValue > endValue) {
				valid = false;
				if (periodOption.endDate) {
					periodOption.endDate.invalid = true;
					periodOption.endDate.errorMessage = 'End date must be on or after start date';
				}
			}
			return valid;
		}

		if (periodOption.year && typeof periodOption.year.validate === 'function') {
			if (!periodOption.year.validate()) {
				valid = false;
			}
		}

		if (periodOption.required) {
			var hasMonth = periodOption.getSelectedCode(periodOption.month) != null;
			var hasQuarter = periodOption.getSelectedCode(periodOption.quarter) != null;
			if (!hasMonth && !hasQuarter) {
				valid = false;
				var periodMessage = 'Select a month or a quarter';
				if (periodOption.month) {
					periodOption.month.invalid = true;
					periodOption.month.errorMessage = periodMessage;
				}
				if (periodOption.quarter) {
					periodOption.quarter.invalid = true;
					periodOption.quarter.errorMessage = periodMessage;
				}
			} else {
				periodOption.clearMonthQuarterInvalid();
			}
		}

		return valid;
	}

	setCustomValue(value) {
		var periodOption = this;
		if (!value) {
			return;
		}

		// Support object (preferred) or legacy "mode,year,month,quarter,start,end" string.
		var data = value;
		if (typeof value === 'string') {
			try {
				data = JSON.parse(value);
			} catch (parseError) {
				var parts = value.split(',');
				data = {
					mode: parts[0] || 'PERIOD',
					year: parts[1] || null,
					month: parts[2] || null,
					quarter: parts[3] || null,
					startDate: parts[4] || null,
					endDate: parts[5] || null
				};
			}
		}

		periodOption.mode = data.mode || 'PERIOD';
		if (periodOption.modeCombo) {
			periodOption.modeCombo.value = periodOption.mode;
		}
		periodOption.updateModeVisibility();

		if (periodOption.year && data.year) {
			periodOption.year.value = '' + data.year;
			var event = new Event('change');
			periodOption.year.dispatchEvent(event);
		}
		if (periodOption.month) {
			periodOption.month.value = data.month != null && data.month !== '' ? '' + data.month : null;
		}
		if (periodOption.quarter) {
			periodOption.quarter.value = data.quarter != null && data.quarter !== '' ? '' + data.quarter : null;
		}
		if (periodOption.startDate && data.startDate) {
			periodOption.startDate.value = data.startDate.indexOf('T') >= 0
				? TimeUtil.formatDateForDateLoadStriped(data.startDate)
				: data.startDate;
		}
		if (periodOption.endDate && data.endDate) {
			periodOption.endDate.value = data.endDate.indexOf('T') >= 0
				? TimeUtil.formatDateForDateLoadStriped(data.endDate)
				: data.endDate;
		}
	}

	getCustomValue() {
		var periodOption = this;
		var mode = periodOption.getSelectedCode(periodOption.modeCombo) || periodOption.mode || 'PERIOD';

		return {
			mode: mode,
			year: periodOption.getSelectedCode(periodOption.year),
			month: periodOption.getSelectedCode(periodOption.month),
			quarter: periodOption.getSelectedCode(periodOption.quarter),
			startDate: periodOption.startDate ? periodOption.startDate.value : null,
			endDate: periodOption.endDate ? periodOption.endDate.value : null
		};
	}
}

customElements.define('dl-option-period-or-daterange', OptionPeriodOrDateRange);
